// Not imported at the top: importing 'logrocket' immediately fetches its script
// from LogRocket's CDN. It is required only in initLogRocket, after consent.
type LogRocketApi = typeof import('logrocket');
let LogRocket: LogRocketApi | null = null;

// LogRocket session replay + error capture. Errors and warnings from
// utils/logger are forwarded here as well (see forwardToLogRocket).
// Started only after cookie consent is accepted (index.tsx, CookieConsent).
// Until then every function below is a no-op.
const LOGROCKET_APP_ID = 'rtrye5/binah';
const APP_VERSION: string = require('../../package.json').version;

// Our API carries names, emails, phone numbers and vital-sign results: keep
// the URL/status of those calls but never send their bodies to LogRocket.
const isOwnApi = (url?: string) => !!url && url.includes('zakhealth.com/api/');

let started = false;

export const initLogRocket = () => {
    if (started) return;
    try {
        const mod = require('logrocket');
        LogRocket = mod.default ?? mod;
        LogRocket.init(LOGROCKET_APP_ID, {
            release: APP_VERSION,
            dom: {
                // Mask everything typed into inputs (name, email, phone, patient id)
                inputSanitizer: true,
            },
            network: {
                requestSanitizer: (request) => {
                    if (isOwnApi(request.url)) request.body = undefined;
                    return request;
                },
                responseSanitizer: (response) => {
                    if (isOwnApi(response.url)) response.body = undefined;
                    return response;
                },
            },
        });
        started = true;
        identifyLogRocketUser();
    } catch {
        // Recording must never break the app
    }
};

// Identify by ids only (no name/email). Call again after login.
export const identifyLogRocketUser = () => {
    if (!started || !LogRocket) return;
    try {
        const user = JSON.parse(localStorage.getItem('userDetails') || 'null');
        const client = JSON.parse(localStorage.getItem('clientDetails') || 'null');
        const clientId = user?.client_id ?? client?.id;
        if (user?.id) {
            LogRocket.identify(String(user.id), {
                clientId: clientId ?? '',
                client: client?.client_website_link ?? '',
            });
        }
    } catch {
        // ignore
    }
};

const toExtra = (details?: Record<string, unknown>) => {
    const extra: Record<string, string> = {};
    Object.entries(details || {}).forEach(([key, value]) => {
        extra[key] = typeof value === 'string' ? value : JSON.stringify(value);
    });
    return extra;
};

export const forwardToLogRocket = (
    level: 'warn' | 'error',
    event: string,
    details?: Record<string, unknown>,
    pageRef?: string,
) => {
    if (!started || !LogRocket) return;
    try {
        LogRocket.captureMessage(`${level}: ${event}`, {
            tags: { level, event, pageRef: pageRef ?? '' },
            extra: toExtra(details),
        });
    } catch {
        // ignore
    }
};

export const captureExceptionInLogRocket = (error: unknown, extra?: Record<string, unknown>) => {
    if (!started || !LogRocket || !(error instanceof Error)) return;
    try {
        LogRocket.captureException(error, { extra: toExtra(extra) });
    } catch {
        // ignore
    }
};
